import {
  Button,
  FormGroup,
  Input,
  Label,
  Row,
  Select,
  Textarea,
} from '@/components'
import { useAxios } from '@/context/axiosContext'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Col } from 'styled-bootstrap-grid'
import { useDialog } from '@/context/dialogContext'
import { useAppSelector } from '@/redux/hooks'
import styled from 'styled-components'
import { formatDate } from '@/utils/date'
import commonUtils from '@/utils/commonUtils'

type TInput = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement

export const TextareaContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

export const AuthorInfo = styled.div`
  width: 100%;
  font-size: 14px;
  color: #555;
  margin-bottom: 4px;
`

export const TextareaWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  width: 100%;
`

export const StyledTextarea = styled.textarea`
  flex: 1;
  padding: 25px;
  font-size: 15px;
  border: 1px solid #ccc;
  border-radius: 4px;
  resize: none;

  &:disabled {
    color: #999;
    cursor: not-allowed;
  }
`

export const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
`

export const DtlButton = styled.button<{
  color?: string
  disabled?: boolean
  content?: string
  display?: string
}>`
  padding: 8px;
  margin-bottom: 4px;
  background-color: ${({ color }) => color || '#007bff'};
  color: white;
  border: none;
  border-radius: 4px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
  position: relative;
  display: ${({ display }) =>
    display || 'block'}; /* 버튼의 display 속성 설정 */

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    opacity: ${({ disabled }) => (disabled ? 0.6 : 0.8)};
  }

  &::after {
    content: '${({ content }) => content || ''}';
    display: block;
    font-size: 12px;
    margin-top: 4px;
    color: white;
  }
`

interface InqrDetailItem {
  cmpnyCd: string
  nttSn: number
  bbsTy: string
  lclasCd: string
  mlsfcCd: string
  sclasCd: string
  bbsSj: string
  nttSj: string
  nttCn: string
  fileSeq: number
  readngCnt: number
  answerCnt: number
  cstId: string
  guestName: string
  guestMbl: string
  guestEml: string
  accessIp: string
  useYn: string
  regId: string
  regDate: string
  updId: string
  updDate: string
  delYn: string // 여기서부터 추가
  replyYn: string
  replyCn: string
  replyRegId: string
  replyRegDate: string
  replyUpdId: string
  replyUpdDate: string
}

interface InqrCommentItem {
  cmpnyCd: string
  nttSn: number
  commentSn: number
  commentCn: string
  cstId: string
  accessIp: string
  useYn: string
  regId: string
  regDate: string
  updId: string
  updDate: string
}

interface ReplyItem {
  cmpnyCd?: string
  nttSn?: number
  replyYn?: string
  replyCn: string
  useYn?: string
  replyRegId?: string
  replyUpdId?: string
  updDate?: string
}

interface NewCommentItem {
  cmpnyCd: string
  nttSn: number
  commentCn: string
  cstId: string
  regId: string
}

const DetailInqrMgmt: React.FC = () => {
  const axios = useAxios()
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAppSelector((state) => state.auth)
  const { showAlert, showConfirm } = useDialog()
  const [requestData] = useState({
    nttSn: location.state?.nttSn || '',
  })

  const [inqr, setInqr] = useState<InqrDetailItem>({
    cmpnyCd: '',
    nttSn: 0,
    bbsTy: '',
    lclasCd: '',
    mlsfcCd: '',
    sclasCd: '',
    bbsSj: '',
    nttSj: '',
    nttCn: '',
    fileSeq: 0,
    readngCnt: 0,
    answerCnt: 0,
    cstId: '',
    guestName: '',
    guestMbl: '',
    guestEml: '',
    accessIp: '',
    useYn: '',
    regId: '',
    regDate: '',
    updId: '',
    updDate: '',
    delYn: '', // 여기서부터 추가
    replyYn: '',
    replyCn: '',
    replyRegId: '',
    replyRegDate: '',
    replyUpdId: '',
    replyUpdDate: '',
  })
  const [comments, setComments] = useState<InqrCommentItem[]>([])
  const [newComment, setNewComment] = useState<NewCommentItem>({
    cmpnyCd: '001',
    nttSn: 0,
    commentCn: '',
    cstId: user.cstId,
    regId: user.userId,
  })

  const [reply, setReply] = useState<ReplyItem>({
    nttSn: requestData.nttSn,
    replyYn: 'Y',
    replyCn: '',
    replyRegId: user.userId,
    replyUpdId: user.userId,
  })

  //펼쳐보기
  const [isVisible, setIsVisible] = useState<boolean>(false)

  const getInqrDetail = async () => {
    try {
      const response = await axios.get('/inqrMgmt/detail', {
        params: { nttSn: requestData.nttSn },
      })
      const common = response.data.common
      if (common.resultCode == 'INQRS0202') {
        const data = response.data.data
        const inqrData = data.inqrDtl
        setInqr(inqrData)
        setNewComment((prev) => {
          return {
            ...prev,
            ['cmpnyCd']: inqrData.cmpnyCd,
            ['nttSn']: inqrData.nttSn,
          }
        })
        if (data.inqrComments) {
          setComments(data.inqrComments)
        }
      } else {
        const closeFunc = () => {
          navigate('/inqrMgmt', {
            state: {
              srchData: location.state?.srchData,
              page: location.state?.page || 1,
              perPage: location.state?.perPage || 1,
            },
          })
        }
        showAlert({
          title: common.resultCode,
          message: common.resultMsg,
          onClose: closeFunc,
        })
      }
    } catch (error) {
      console.error('Error fetching terms info:', error)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<TInput>) => {
    const { name, value } = e.target
    setInqr((prevState) => {
      return {
        ...prevState,
        [name]: value,
        ['updId']: user.userId,
      }
    })
  }

  const handleTextAreaChange = (
    e: React.ChangeEvent<TInput>,
    idx: number | undefined,
    flag?: 'reply',
  ) => {
    const { name, value } = e.target
    if (idx) {
      setComments((prev) =>
        prev?.map((item, index) =>
          idx === index ? { ...item, [name]: value } : item,
        ),
      )
    } else if (flag === 'reply') {
      setReply((prev) => {
        return {
          ...prev,
          [name]: value,
        }
      })
    } else {
      setNewComment((prev) => {
        return {
          ...prev,
          [name]: value,
        }
      })
    }
  }

  const insertComment = async () => {
    try {
      if (newComment.commentCn.trim().length == 0) {
        showAlert({ type: 'warning', message: '댓글을 입력해주세요.' })
        return
      }
      const response = await axios.post('/inqrMgmt/insertCmt', newComment)
      const common = response.data.common
      if (common.resultCode === 'INQRI0200') {
        const data = response.data.data
        setComments(data.inqrComments)
        setNewComment((prev) => {
          return {
            ...prev,
            ['commentCn']: '',
          }
        })
      } else {
        showAlert({
          message: common.resultMsg,
          title: common.resultCode,
        })
      }
    } catch (error) {
      console.error(error)
    }
  }

  //어드민 답변추가
  const insertReply = async () => {
    try {
      if (reply.replyCn.trim().length == 0) {
        showAlert({ type: 'warning', message: '답변을 입력해주세요.' })
        return
      }

      const response = await axios.post('/inqrMgmt/insertReply', reply)
      const common = response.data.common
      if (common.resultCode === 'INQRI0202') {
        const data = response.data.data
        // setNewComment((prev) => {
        //   return {
        //     ...prev,
        //     ['commentCn']: '',
        //   }
        // })
        showAlert({
          type: 'success',
          message: '답변이 등록되었어요.',
          title: '성공',
          onClose() {
            getInqrDetail()
          },
        })
      } else {
        showAlert({
          type: 'error',
          message: common.resultMsg,
          title: common.resultCode,
        })
      }
    } catch (error) {
      console.error(error)
    }
  }

  const updateInqr = async () => {
    try {
      const response = await axios.post('/inqrMgmt/inqrUpdate', inqr)
      const common = response.data.common
      if (common.resultCode == 'INQRU0200') {
        showAlert({
          type: 'success',
          message: '정상적으로 수정되었습니다.',
          title: '성공',
          onClose() {
            navigate('/inqrMgmt', {
              state: {
                srchData: location.state?.srchData,
                page: location.state?.page || 1,
                perPage: location.state?.perPage || 1,
              },
            })
          },
        })
      } else {
        showAlert({
          type: 'error',
          message: common.resultMsg || '문의사항 업데이트 실패',
          title: common.resultCode,
        })
      }
    } catch (error) {
      console.error('Error fetching terms reg:', error)
    }
  }

  const toggleVisibility = () => {
    setIsVisible((prevState) => !prevState)
  }

  const updateComment = (idx: number, type: 'mod' | 'del') => {
    const sendData = comments[idx]
    if (type === 'mod' && sendData.commentCn.trim().length == 0) {
      showAlert({
        type: 'warning',
        message: '수정하는 댓글 내용을 확인해주세요.',
      })
      return
    }
    const msg =
      type === 'mod' ? '수정하시겠습니까?' : '댓글을 삭제하시겠습니까?'
    const confirmFunc = async () => {
      sendData['useYn'] = 'N'
      sendData['updId'] = user.userId
      const response = await axios.post('/inqrMgmt/cmtUpdate', sendData)
      const common = response.data.common
      if (common.resultCode === 'INQRU0202') {
        if (type === 'del') {
          setComments((prev) =>
            prev.map((comment, index) => (idx === index ? sendData : comment)),
          )
        }
      } else {
        showAlert({
          type: 'error',
          message: common.resultMsg,
          title: common.resultCode,
        })
      }
    }

    showConfirm({ message: msg, title: undefined, onConfirm: confirmFunc })
  }

  useEffect(() => {
    getInqrDetail()
  }, [])

  return (
    <>
      <div style={{ width: '100%', height: '100%', marginTop: '50px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            marginBottom: '10px',
            alignItems: 'center',
            marginTop: '50px',
          }}
        >
          <p style={{ fontSize: '20px' }}>문의게시판 관리</p>
          <div style={{ display: 'flex', gap: '5px' }}>
            <Button
              variant="outlined"
              color="neverbe"
              onClick={toggleVisibility}
            >
              {isVisible ? '접기' : '전체보기'}
            </Button>
            <Button
              color="neverbe"
              onClick={() => {
                showConfirm({
                  title: '문의관리 화면으로 이동할까요?',
                  onConfirm() {
                    navigate('/inqrMgmt', {
                      state: {
                        srchData: location.state?.srchData,
                        page: location.state?.page || 1,
                        perPage: location.state?.perPage || 1,
                      },
                    })
                  },
                })
              }}
            >
              목록
            </Button>
            <Button
              onClick={() => {
                updateInqr()
              }}
              color="neverbe"
            >
              저장
            </Button>
          </div>
        </div>
      </div>
      <div>
        <Row>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="nttSn">
                번호
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <Input
                id="nttSn"
                name="nttSn"
                type="text"
                maxLength={10}
                value={inqr.nttSn}
                disabled={true}
              />
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="regId">
                작성자
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <Input
                id="regId"
                name="regId"
                type="text"
                placeholder=""
                maxLength={3}
                value={
                  inqr.regId +
                  `(${inqr?.cstId && inqr.cstId != '' ? '회원' : '비회원'})`
                }
                disabled={true}
              />
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="regDate">
                작성일
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <Input
                id="regDate"
                name="regDate"
                type="text"
                placeholder=""
                maxLength={3}
                value={formatDate(inqr.regDate)}
                disabled={true}
              />
            </FormGroup>
          </Col>
        </Row>
        {isVisible && (
          <Row>
            <Col col>
              <FormGroup mb="20px">
                <Label display="block" mb="5px" htmlFor="bbsSj">
                  카테고리
                </Label>
                <div style={{ marginBottom: '5px' }} />
                <Input
                  id="bbsSj"
                  name="bbsSj"
                  type="text"
                  maxLength={250}
                  value={inqr.bbsSj}
                  disabled={true}
                />
              </FormGroup>
            </Col>
            <Col col>
              <FormGroup mb="20px">
                <Label display="block" mb="5px" htmlFor="bbsTy">
                  게시판 유형
                </Label>
                <Input
                  id="bbsTy"
                  name="bbsTy"
                  type="text"
                  maxLength={250}
                  value={inqr.bbsTy}
                  disabled={true}
                />
              </FormGroup>
            </Col>
            <Col col>
              <FormGroup mb="20px">
                <Label display="block" mb="5px" htmlFor="lclasCd">
                  게시물 대분류 코드
                </Label>
                <Input
                  id="lclasCd"
                  name="lclasCd"
                  type="text"
                  maxLength={250}
                  value={inqr.lclasCd || '00'}
                  disabled={true}
                />
              </FormGroup>
            </Col>
            <Col col>
              <FormGroup mb="20px">
                <Label display="block" mb="5px" htmlFor="mlsfcCd">
                  게시물 중분류 코드
                </Label>
                <Input
                  id="mlsfcCd"
                  name="mlsfcCd"
                  type="text"
                  maxLength={250}
                  value={inqr.mlsfcCd || '00'}
                  disabled={true}
                />
              </FormGroup>
            </Col>
          </Row>
        )}
        <Row>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="nttSj">
                제목
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <Input
                id="nttSj"
                name="nttSj"
                type="text"
                maxLength={250}
                value={inqr.nttSj}
                disabled={true}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="nttCn">
                문의 내용
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <Textarea
                id="nttCn"
                name="nttCn"
                value={inqr.nttCn}
                disabled={true}
                height={'300px'}
              />
            </FormGroup>
          </Col>
        </Row>
        {inqr.replyYn === 'Y' && (
          <Row>
            <Col col>
              <FormGroup mb="20px">
                <Label display="block" mb="5px" htmlFor="replyCn">
                  답변 내용
                </Label>
                <div style={{ marginBottom: '5px' }} />
                <Textarea
                  id="replyCn"
                  name="replyCn"
                  value={inqr.replyCn}
                  disabled={true}
                  height={'200px'}
                />
              </FormGroup>
            </Col>
          </Row>
        )}
        <Row>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="cntArea">
                답변 작성
              </Label>
              <Label display="block" mb="5px" htmlFor="cntArea">
                <div
                  style={{
                    marginLeft: '5px',
                    color: '#d09d30',
                    marginTop: '5px',
                    textAlign: 'right',
                  }}
                >
                  * 수정/작성 후에는 저장 버튼을 눌러주세요.
                </div>
              </Label>

              <div style={{ marginBottom: '5px' }} />
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'stretch',
                  gap: '5px',
                }}
              >
                <Textarea
                  id="replyCn"
                  name="replyCn"
                  value={reply.replyCn}
                  height={'300px'}
                  onChange={(e) => {
                    handleTextAreaChange(e, undefined, 'reply')
                  }}
                />
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '5px',
                    height: '300px',
                    width: '70px',
                  }}
                >
                  <ButtonContainer style={{ height: '100%' }}>
                    <DtlButton
                      color="#d09d30"
                      onClick={insertReply}
                      style={{
                        height: '100%',
                        backgroundColor: 'transparent',
                        border: '1px solid #d09d30',
                        color: '#d09d30',
                      }}
                    >
                      답변저장
                    </DtlButton>
                  </ButtonContainer>
                </div>
              </div>
              {/* <TextareaContainer>
                <TextareaWrapper>
                  <StyledTextarea
                    name="replyCn"
                    value={reply.replyCn}
                    onChange={(e) => {
                      handleTextAreaChange(e, undefined, 'reply')
                    }}
                    height={'300px'}
                  />
                  <ButtonContainer>
                    <DtlButton color="#4CAF50" onClick={insertReply}>
                      작성
                    </DtlButton>
                  </ButtonContainer>
                </TextareaWrapper>
              </TextareaContainer> */}
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="useYn">
                사용여부
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <Select
                id="useYn"
                name="useYn"
                value={inqr.useYn}
                onChange={handleInputChange}
              >
                <option value={'Y'}>Y</option>
                <option value={'N'}>N</option>
              </Select>
            </FormGroup>
          </Col>
          <Col col>
            {/* <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="updDate">
                최종 수정 일시
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <Input
                id="updDate"
                name="updDate"
                type="text"
                maxLength={100}
                value={formatDate(inqr.updDate)}
                disabled={true}
              />
            </FormGroup> */}
          </Col>
        </Row>
        <Row>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="regId">
                등록자
              </Label>
              <Input
                id="regId"
                name="regId"
                type="text"
                placeholder=""
                maxLength={100}
                value={inqr.regId}
                disabled={true}
                // onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="regDate">
                등록일시
              </Label>
              <Input
                id="regDate"
                name="regDate"
                type="text"
                placeholder=""
                maxLength={100}
                value={commonUtils.formatDate(inqr.regDate, true)}
                disabled={true}
                // onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="updId">
                수정자
              </Label>
              <Input
                id="updId"
                name="updId"
                type="text"
                placeholder=""
                maxLength={100}
                value={inqr.updId}
                disabled={true}
                // onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="updDate">
                수정일시
              </Label>
              <Input
                id="updDate"
                name="updDate"
                type="text"
                placeholder=""
                maxLength={100}
                value={commonUtils.formatDate(inqr.updDate, true)}
                disabled={true}
                // onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
        </Row>
        {/* <Row>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="cntArea">
                댓글
              </Label>
              {comments &&
                comments.map((comment, idx) => {
                  return (
                    <TextareaContainer key={idx}>
                      <AuthorInfo>
                        작성자: {comment.regId} | 작성일:{' '}
                        {formatDate(comment.regDate)}
                        {comment.useYn === 'N' ? ' | 삭제된 댓글' : ''}
                      </AuthorInfo>
                      <TextareaWrapper>
                        <StyledTextarea
                          name="commentCn"
                          value={comment.commentCn}
                          readOnly={!(comment.regId == user.userId)}
                          disabled={!(comment.useYn == 'Y')}
                          onChange={(e) => {
                            handleTextAreaChange(e, idx)
                          }}
                        />
                        <ButtonContainer>
                          <DtlButton
                            display={
                              comment.regId == user.userId &&
                              comment.useYn == 'Y'
                                ? 'block'
                                : 'none'
                            }
                            color="#4CAF50"
                            onClick={() => {
                              updateComment(idx, 'mod')
                            }}
                          >
                            수정
                          </DtlButton>
                          <DtlButton
                            display={comment.useYn == 'Y' ? 'block' : 'none'}
                            color="#f44336"
                            onClick={() => {
                              updateComment(idx, 'del')
                            }}
                          >
                            삭제
                          </DtlButton>
                        </ButtonContainer>
                      </TextareaWrapper>
                    </TextareaContainer>
                  )
                })}
            </FormGroup>
          </Col>
        </Row> */}
        {/* <Row>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="cntArea">
                댓글 작성
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <TextareaContainer>
                <TextareaWrapper>
                  <StyledTextarea
                    name="commentCn"
                    value={newComment.commentCn}
                    onChange={(e) => {
                      handleTextAreaChange(e, undefined)
                    }}
                  />
                  <ButtonContainer>
                    <DtlButton color="#4CAF50" onClick={insertComment}>
                      작성
                    </DtlButton>
                  </ButtonContainer>
                </TextareaWrapper>
              </TextareaContainer>
            </FormGroup>
          </Col>
        </Row> */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'right',
            width: '100%',
            marginBottom: '10px',
            alignItems: 'center',
          }}
        >
          <div style={{ display: 'flex', gap: '5px' }}>
            <Button
              color="neverbe"
              onClick={() => {
                showConfirm({
                  title: '문의관리 화면으로 이동할까요?',
                  onConfirm() {
                    navigate('/inqrMgmt', {
                      state: {
                        srchData: location.state?.srchData,
                        page: location.state?.page || 1,
                        perPage: location.state?.perPage || 1,
                      },
                    })
                  },
                })
              }}
            >
              목록
            </Button>
            <Button
              onClick={() => {
                updateInqr()
              }}
              color="neverbe"
            >
              저장
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}
export default DetailInqrMgmt
